import { useEffect } from 'react';
import { supportApi } from '../lib/support-api.js';
import { supportToken } from '../lib/fleet-token.js';
import { Screen } from '../components/Screen.js';
import { TabNav, type RecordTabs } from '../components/Record.js';
import { SupportCategoriesScreen } from './SupportCategories.js';
import { SupportMappingScreen } from './SupportMapping.js';
import { VolumeAlertPanel } from './SupportReview.js';
import { SuspendedIpDetailScreen, SuspendedIpsScreen } from './SuspendedIps.js';

/**
 * Support > Configuration (Jon, 2026-10-11 and 2026-10-12): how the support
 * desk is set up, apart from the work itself.
 *
 *   `#/support-configuration/categories`     the tree tickets are filed under (support#68)
 *   `#/support-configuration/mapping`        which form choices file a ticket where
 *   `#/support-configuration/settings`       the support emails staff get: the volume alert (support#60)
 *   `#/support-configuration/suspended-ips`  sign-in bans (auth#587; the data is auth's)
 *
 * Bare `#/support-configuration` opens Categories. The page was called
 * Settings for a day (`#/support-settings`, with a Configuration tab); those
 * links are sent on here by App.
 */
const api = supportApi(supportToken);

type Section = 'categories' | 'mapping' | 'settings' | 'suspended-ips';
const TABS: RecordTabs<Section> = [
  { path: 'categories', label: 'Categories' },
  { path: 'mapping', label: 'Form mapping' },
  { path: 'settings', label: 'Settings' },
  { path: 'suspended-ips', label: 'Suspended IPs' },
];

export const supportConfigurationPath = (section: Section = 'categories') => `/support-configuration/${section}`;

/**
 * Where a link to the old Settings page, or to a section that once lived on
 * the Support page, goes now. `configuration` was this page's Settings tab.
 */
export function movedSupportPath(section: string | undefined, detail: string | undefined): string {
  const renamed = section === 'configuration' ? 'settings' : (section ?? 'categories');
  return `/support-configuration/${renamed}${detail ? `/${encodeURIComponent(detail)}` : ''}`;
}

export function SupportConfiguration({
  section: requested,
  detail,
  navigate,
}: {
  section?: string;
  /** `#/support-configuration/suspended-ips/<network>`: one network. */
  detail?: string;
  navigate: (to: string) => void;
}) {
  const section = TABS.find((t) => t.path === (requested ?? 'categories'))?.path;
  const tabs = <TabNav base="/support-configuration" tabs={TABS} current={section ?? 'categories'} label="Support configuration" />;

  if (!section) return <Redirect to={supportConfigurationPath()} navigate={navigate} />;
  if (section === 'mapping') return <SupportMappingScreen api={api} tabs={tabs} />;
  if (section === 'settings') {
    return (
      <Screen filters={tabs}>
        <VolumeAlertPanel api={api} />
      </Screen>
    );
  }
  if (section === 'suspended-ips' && detail) {
    return <SuspendedIpDetailScreen key={detail} network={detail} tabs={tabs} navigate={navigate} />;
  }
  if (section === 'suspended-ips') return <SuspendedIpsScreen tabs={tabs} navigate={navigate} />;
  return <SupportCategoriesScreen api={api} tabs={tabs} />;
}

/** Sends the browser on to `to`, once, and draws nothing. */
export function Redirect({ to, navigate }: { to: string; navigate: (to: string) => void }) {
  useEffect(() => navigate(to), [to, navigate]);
  return null;
}
